import React, { useEffect, useState } from 'react';
import { reduxForm, Field, Form, InjectedFormProps } from 'redux-form';
import Fields from 'components/Fields';
import { Button, Loader } from 'components/Base';
import Router from 'components/Router/Router';
import Logo from 'assets/images/fintegrateLogo.png';
import { ReactComponent as GeometricBackground } from 'assets/images/geometricBackground.svg';
import { makeStyles } from '@material-ui/core';
import Icons from 'components/Icons';
import classNames from 'classnames';
import api from 'api';
import { useAuth } from 'providers/AuthProvider/SessionAuthProvider';

const { useHistory, useParams } = Router;

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(#fafafa 60%, #efefef 60%)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  background: {
    position: 'fixed',
    width: '100%',
    bottom: '40%'
  },
  logo: {
    maxWidth: '300px',
    maxHeight: '100%',
    height: 'auto',
    paddingTop: '2rem',
    margin: '0 auto'
  },
  box: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
    borderRadius: '25px',
    maxWidth: '500px',
    width: '100%'
  },
  form: {
    padding: '1rem'
  },
  link: {
    marginLeft: 'auto',
    color: '#5989c5',
    cursor: 'pointer',
    '&:hover': {
      color: '#36649b'
    }
  },
  flex: {
    display: 'flex',
    alignItems: 'center'
  },
  error: {
    color: '#ed4337'
  },
  addons: {
    fontSize: '14px',
    fontWeight: 600
  }
});

const PasswordResetPage = ({ handleSubmit }: InjectedFormProps) => {
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();
  const classes = useStyles();
  const { userId, token } = useParams() as any;
  const { getUser, setCsrfFromHeader, isAuthenticated } = useAuth();

  const submit = (values: any) => {
    setSubmitting(true);
    api
      .resetPassword(userId, values.password, values.confirm_password)
      .then((res: any) => {
        setSubmitted(true);
        getUser();
        setCsrfFromHeader(res);
      })
      .catch(error => {
        setHasError(true);
        setError(error.response.data.non_field_errors[0]);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    api.checkPasswordReset(userId, token).then(({ data }) => {
      setValid(data.valid);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Loader isLoading={loading}>
      <div className={classes.root}>
        <GeometricBackground className={classes.background} color="#efefef" />
        <div className={classes.box}>
          <img className={classes.logo} src={Logo} alt="Fintegrate Logo" />
          {valid && (
            <>
              {!submitted && (
                <div className={classes.form}>
                  <h2>Reset your password</h2>
                  <Form onSubmit={handleSubmit(submit)}>
                    <Field
                      name="password"
                      component={Fields.Text}
                      label="New password"
                      type="password"
                      required
                    />
                    <Field
                      style={{ margin: '1rem 0', width: '100%' }}
                      name="confirm_password"
                      component={Fields.Text}
                      label="Confirm your password"
                      type="password"
                      required
                    />
                    <div className={classNames(classes.addons, classes.flex)}>
                      {hasError && (
                        <div
                          className={classNames(classes.error, classes.flex)}
                        >
                          <Icons.ErrorIcon
                            style={{ marginRight: '0.25rem' }}
                            fill="#ed4337"
                          />
                          <div>{error}</div>
                        </div>
                      )}
                    </div>

                    <Button isLoading={submitting} type="submit">
                      Continue
                    </Button>
                  </Form>
                </div>
              )}
              {submitted && (
                <div className={classes.form}>
                  <h2>Password changed successfully</h2>
                  <Button
                    isLoading={!isAuthenticated}
                    type="button"
                    onClick={() => {
                      history.push('/');
                    }}
                  >
                    Continue to Dashboard
                  </Button>
                </div>
              )}
            </>
          )}
          {!valid && (
            <div className={classes.form}>
              <h2>Reset your password</h2>
              <p>
                We were unable to confirm your identity. Either the link you
                followed is invalid, or it has expired. Please{' '}
                <a href="/login" className={classNames(classes.link)}>
                  try again
                </a>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </Loader>
  );
};

export default reduxForm({
  form: 'password-reset',
  enableReinitialize: true
})(PasswordResetPage);
